import { Trophy, Lock, Volume2 } from 'lucide-react';

import {
  RARITY_STYLES,
  RARITY_BG,
  BADGE_SIZES,
} from '@/constants/badge';

/**
 * Unified BadgeCard Component
 *
 * @param {Object} badge - Badge data object
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} showProgress - Show progress bar for locked badges
 * @param {boolean} showAudio - Show audio button if audio_url exists
 * @param {Function} onClick - Click handler
 * @param {Function} onPlayAudio - Audio play handler
 * @param {boolean} isNew - Animate as newly earned
 * @param {string} className - Additional classes
 */
const BadgeCard = ({
  badge,
  size = 'md',
  showProgress = true,
  showAudio = true,
  onClick,
  onPlayAudio,
  isNew = false,
  className = '',
}) => {
  const isSecret = badge.is_secret && !badge.earned;
  const sizeConfig = BADGE_SIZES[size] || BADGE_SIZES.md;

  // Audio should show for earned badges (including earned secrets) if audio_url exists
  const shouldShowAudio = showAudio && badge.audio_url && (!badge.is_secret || badge.earned);

  const handleAudioClick = (e) => {
    e.stopPropagation();
    if (onPlayAudio && badge.audio_url) {
      onPlayAudio(badge.audio_url);
    }
  };

  // Calculate progress percentage
  const progressPercentage = badge.progress?.percentage ??
    (badge.progress?.current && badge.progress?.target
      ? (badge.progress.current / badge.progress.target) * 100
      : 0);

  return (
    <button
      onClick={onClick}
      className={`
        relative rounded-xl text-center transition-all duration-200
        hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500
        ${sizeConfig.container}
        ${badge.earned
          ? `${RARITY_BG[badge.rarity] || RARITY_BG.common} ${RARITY_STYLES[badge.rarity] || RARITY_STYLES.common}`
          : 'bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        }
        ${isNew ? 'animate-badge-earned' : ''}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Badge emoji */}
      <span
        className={`
          block mb-1
          ${sizeConfig.emoji}
          ${!badge.earned ? 'grayscale opacity-50' : ''}
        `.trim()}
      >
        {isSecret ? '❓' : badge.icon_emoji}
      </span>

      {/* Badge name */}
      <p
        className={`
          font-medium line-clamp-1
          ${sizeConfig.name}
          ${!badge.earned ? 'text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-100'}
        `.trim()}
      >
        {isSecret ? '???' : badge.name}
      </p>

      {/* Progress bar for locked, non-secret badges */}
      {!badge.earned && badge.progress && !isSecret && showProgress && progressPercentage > 0 && (
        <div className={`mt-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${sizeConfig.progressHeight}`}>
          <div
            className="h-full bg-purple-400 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          />
        </div>
      )}

      {/* Earned trophy indicator - top right */}
      {badge.earned && (
        <div className={`absolute -top-1 -right-1 bg-green-500 rounded-full ${sizeConfig.trophyPadding}`}>
          <Trophy className={`text-white ${sizeConfig.trophyIcon}`} />
        </div>
      )}

      {/* Locked indicator for non-secret locked badges */}
      {!badge.earned && !isSecret && (
        <div className="absolute -top-1 -right-1 bg-gray-400 rounded-full p-0.5">
          <Lock className="h-2.5 w-2.5 text-white" />
        </div>
      )}

      {/* Audio button - bottom right */}
      {shouldShowAudio && (
        <button
          onClick={handleAudioClick}
          className={`
            absolute -bottom-1 -right-1 rounded-full
            bg-purple-100 dark:bg-purple-900/50 hover:bg-purple-200 dark:hover:bg-purple-800 text-purple-600 dark:text-purple-400
            transition-colors p-1
            ${size === 'sm' ? 'p-0.5' : ''}
          `.trim()}
          title="Play pronunciation"
        >
          <Volume2 className={sizeConfig.audioButton} />
        </button>
      )}
    </button>
  );
};

export default BadgeCard;
